import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterPanel, SelectFilter } from '@/components/FilterPanel'
import { SearchInput } from '@/components/SearchInput'
import { StatusBadge } from '@/components/StatusBadge'
import { UserAvatar } from '@/components/UserAvatar'
import { Drawer } from '@/components/Drawer'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { IconCopy } from '@/components/Icons'
import { toast } from '@/components/Toast'
import { useSignatures, useOrganizations } from '@/hooks/useRootQueries'
import type { SignerItem } from '@/types/api'
import { exactTimestamp, relativeTime } from '@/utils/time'
import { formatHash } from '@/utils/format'

export function Signatures() {
  const [params, setParams] = useSearchParams()
  const [selectedSig, setSelectedSig] = useState<SignerItem | null>(null)
  const [activeDocId, setActiveDocId] = useState<string | null>(null)

  const orgsQuery = useOrganizations({ pageSize: 100 })
  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsQuery.data?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
  ]

  const filters = useMemo(
    () => ({
      search: params.get('search') ?? '',
      organization: params.get('organization') ?? '',
      status: params.get('status') ?? '',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    }),
    [params],
  )

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.set('page', '1')
    setParams(next)
  }

  const signatures = useSignatures(filters)

  const columns: Column<SignerItem>[] = [
    {
      key: 'signer',
      header: 'Signer Name / Identity',
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <UserAvatar name={row.name} />
          <div>
            <div className="font-semibold text-slate-900">{row.name}</div>
            <div className="font-mono text-[10.5px] text-slate-500">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <div>
          <div className="font-bold text-slate-800 text-xs truncate max-w-[150px]">{row.orgName || 'Organization'}</div>
          {row.orgId && (
            <div className="font-mono text-[10px] text-slate-400 truncate max-w-[120px]">ID: {row.orgId.slice(-6)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'docId',
      header: 'Document Reference',
      render: (row) => (
        <div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setActiveDocId(row.docId)
            }}
            className="font-semibold text-sky-700 hover:text-sky-900 hover:underline truncate max-w-[200px] block text-left"
            title={row.docTitle || row.docId}
          >
            {row.docTitle || 'Untitled Document'}
          </button>
          <div className="font-mono text-[10px] text-slate-400 truncate max-w-[150px]">ID: {row.docId}</div>
        </div>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      render: (row) => (
        <span className="font-mono font-bold text-xs bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md text-slate-700">
          #{row.order || 1}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'hash',
      header: 'Signature Hash',
      render: (row) =>
        row.signatureHash ? (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <span
              className="font-mono text-[11px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded inline-block shadow-2xs select-all cursor-pointer hover:border-sky-400"
              title={`Click to copy SHA-256 hash:\n${row.signatureHash}`}
              onClick={() => {
                navigator.clipboard.writeText(row.signatureHash || '')
                toast.success('Signature hash copied to clipboard!')
              }}
            >
              {formatHash(row.signatureHash, 6, 6)}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(row.signatureHash || '')
                toast.success('Signature hash copied to clipboard!')
              }}
              className="rounded p-1 text-slate-400 hover:text-sky-700 hover:bg-sky-50 transition-colors"
              title="Copy hash"
            >
              <IconCopy className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <span className="text-slate-400 text-[11px]">—</span>
        ),
    },
    {
      key: 'signedAt',
      header: 'Signed Date',
      render: (row) =>
        row.signedAt ? (
          <div>
            <div className="font-semibold text-slate-900">{relativeTime(row.signedAt)}</div>
            <div className="font-mono text-[10px] text-slate-400">{exactTimestamp(row.signedAt)}</div>
          </div>
        ) : (
          <span className="text-slate-400 text-[11px]">—</span>
        ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Cryptographic Signatures</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Cross-tenant recipient signature activity, hash validation, and execution timestamps.
          </p>
        </div>
      </div>

      <FilterPanel>
        <div className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[10.5px] font-bold text-slate-500 uppercase">Search Signatures</span>
          <SearchInput
            value={filters.search}
            onChange={(v) => updateParam('search', v)}
            placeholder="Signer name, email, or document ID…"
          />
        </div>
        <SelectFilter
          label="Organization"
          value={filters.organization}
          options={orgOptions}
          onChange={(v) => updateParam('organization', v)}
        />
        <SelectFilter
          label="Status"
          value={filters.status}
          options={[
            { value: 'signed', label: 'Signed' },
            { value: 'pending', label: 'Pending' },
            { value: 'viewed', label: 'Viewed' },
            { value: 'declined', label: 'Declined' },
          ]}
          onChange={(v) => updateParam('status', v)}
        />
        {(filters.search || filters.organization || filters.status) && (
          <button
            onClick={() => setParams(new URLSearchParams())}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs"
          >
            Reset
          </button>
        )}
      </FilterPanel>

      <DataTable
        columns={columns}
        rows={signatures.data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={signatures.isLoading}
        isError={signatures.isError}
        errorMessage="Could not load signatures."
        onRetry={() => signatures.refetch()}
        emptyTitle="No signatures found"
        emptyDescription="Try adjusting your search or filters."
        onRowClick={(row) => setSelectedSig(row)}
        page={filters.page}
        totalPages={signatures.data?.meta.totalPages ?? 1}
        totalItems={signatures.data?.meta.totalItems ?? 0}
        onPageChange={(p) => updateParam('page', String(p))}
      />

      {/* Signature Detail Drawer */}
      <Drawer
        open={!!selectedSig}
        onClose={() => setSelectedSig(null)}
        title={selectedSig ? `Signer: ${selectedSig.name}` : 'Signature Details'}
      >
        {selectedSig && (
          <div className="space-y-4 font-mono text-xs">
            <div>
              <span className="text-[10.5px] font-bold text-slate-500 block uppercase mb-1">Status</span>
              <StatusBadge status={selectedSig.status} />
            </div>
            <div>
              <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Signer Email</span>
              <span className="text-slate-900 font-semibold">{selectedSig.email}</span>
            </div>
            <div>
              <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Document Reference</span>
              <button
                onClick={() => setActiveDocId(selectedSig.docId)}
                className="text-sky-700 hover:text-sky-900 hover:underline font-bold block text-left"
              >
                {selectedSig.docTitle || `Document #${selectedSig.docId.slice(-6)}`} →
              </button>
            </div>
            <div>
              <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Signing Order</span>
              <span className="text-slate-900 font-semibold">#{selectedSig.order}</span>
            </div>
            {selectedSig.signatureHash && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-sky-900 uppercase">Signature Hash (SHA-256)</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedSig.signatureHash || '')
                      toast.success('Signature hash copied to clipboard!')
                    }}
                    className="text-[10.5px] font-bold text-sky-700 hover:text-sky-900 hover:underline inline-flex items-center gap-1"
                  >
                    <IconCopy className="h-3 w-3" />
                    <span>Copy Hash</span>
                  </button>
                </div>
                <div
                  className="rounded border border-sky-300 bg-white p-2 font-mono text-[11px] font-semibold text-slate-900 select-all shadow-2xs cursor-pointer hover:border-sky-500 transition-colors"
                  title={`Full SHA-256 Hash:\n${selectedSig.signatureHash}`}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedSig.signatureHash || '')
                    toast.success('Signature hash copied to clipboard!')
                  }}
                >
                  {formatHash(selectedSig.signatureHash, 10, 10)}
                </div>
              </div>
            )}
            {selectedSig.ipAddress && (
              <div>
                <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Signer IP Address</span>
                <span className="text-slate-900 font-semibold">{selectedSig.ipAddress}</span>
              </div>
            )}
            {selectedSig.signedAt && (
              <div>
                <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Signed At</span>
                <span className="text-slate-900 font-semibold">{exactTimestamp(selectedSig.signedAt)}</span>
              </div>
            )}
            <div>
              <span className="text-[10.5px] font-bold text-slate-500 block uppercase">Created At</span>
              <span className="text-slate-900 font-semibold">{exactTimestamp(selectedSig.createdAt)}</span>
            </div>
          </div>
        )}
      </Drawer>

      {/* Linked Document Detail Drawer */}
      <DocumentDetailDrawer
        open={!!activeDocId}
        onClose={() => setActiveDocId(null)}
        documentId={activeDocId}
      />
    </div>
  )
}
